import { type FC, useEffect } from 'react'
import { Radio } from '@/components/commons/radio'
import { Separator } from '@/components/ui/separator'
import { CurationPhotosetSpaceImages } from '@/features/photoset/components/curation-photoset-space-images'
import { useAddPhotosetSpaceFlag } from '@/features/photoset/queries/use-add-photoset-space-flag'
import type {
	Image,
	PhotosetSpace,
	PhotosetSpacesChecks,
	Space,
	SpaceConfiguration
} from '@/features/photoset/types.ts'
import { spaceNameToLabel } from '@/features/photoset/utils.ts'
import { cn, errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface UnusableCurationPhotosetSpaceProps {
	type: 'noImages' | 'minimumNotMet'
	spaceName: Space['key_name']
	spaceId: PhotosetSpace['id']
	totalImages: number
	images: Omit<Image, 'space'>[]
	spaceConfigs: SpaceConfiguration
	spaceFlagValue: PhotosetSpace['flag_key_name']
	spaceChecks: PhotosetSpacesChecks
	curationTouched: boolean
	spaceIndex: number
	onExpandImage: (spaceIndex: number, imageIndex: number) => void
	cbSetHideAllUnselected: () => void
}

export const UnusableCurationPhotosetSpace: FC<
	UnusableCurationPhotosetSpaceProps
> = ({
	type,
	spaceName,
	spaceId,
	totalImages,
	images,
	spaceConfigs,
	spaceChecks,
	spaceFlagValue,
	curationTouched,
	spaceIndex,
	cbSetHideAllUnselected,
	onExpandImage
}) => {
	const { t } = useTranslation()
	const { mutate: addPhotosetSpaceFlag } = useAddPhotosetSpaceFlag({
		onError: (error) => errorHandler(error)
	})

	useEffect(() => {
		if (type === 'minimumNotMet' && !spaceFlagValue) {
			addPhotosetSpaceFlag({
				photosetSpaceId: spaceId,
				flag: 'cannot_reach_minimum'
			})
		}
	}, [addPhotosetSpaceFlag, spaceFlagValue, spaceId, type])

	const unusableCurationSpaceBody = () => {
		switch (type) {
			case 'noImages':
				return (
					<div
						className={cn(
							'flex w-full flex-col items-center gap-3',
							totalImages === 0 && 'bg-neutral-100 py-12'
						)}
					>
						<h3 className='font-medium text-lg text-neutral-black'>
							{t('photoset:curation.space.no_images_title')}
						</h3>
						<p className='max-w-[360px] text-center text-neutral-600 text-sm'>
							{t('photoset:curation.space.no_images_desc')}
						</p>
						{type === 'noImages' && (
							<div className='flex gap-4'>
								<Radio
									defaultChecked={
										spaceFlagValue === 'no_assets_delivered'
									}
									label={t(
										'photoset:curation.space.no_assets_delivered'
									)}
									name={`radio-${spaceName}`}
									onChange={() =>
										addPhotosetSpaceFlag({
											photosetSpaceId: spaceId,
											flag: 'no_assets_delivered'
										})
									}
									value={'no_assets_delivered'}
								/>
								<Radio
									defaultChecked={
										spaceFlagValue ===
										'no_elements_applicable'
									}
									label={t(
										'photoset:curation.space.no_applicable_element'
									)}
									name={`radio-${spaceName}`}
									onChange={() =>
										addPhotosetSpaceFlag({
											photosetSpaceId: spaceId,
											flag: 'no_elements_applicable'
										})
									}
									value={'no_elements_applicable'}
								/>
							</div>
						)}
					</div>
				)
			case 'minimumNotMet':
				return (
					<CurationPhotosetSpaceImages
						cbSetHideAllUnselected={cbSetHideAllUnselected}
						images={images}
						onExpandImage={onExpandImage}
						spaceId={spaceId}
						spaceIndex={spaceIndex}
					/>
				)
			default:
				return (
					<div
						className={cn(
							'flex w-full flex-col items-center gap-3',
							totalImages === 0 && 'bg-neutral-100 py-12'
						)}
					>
						<h3 className='font-medium text-lg text-neutral-black'>
							{t('photoset:curation.space.minimum_not_met_title')}
						</h3>
						<p className='max-w-[360px] text-center text-neutral-600 text-sm'>
							{t('photoset:curation.space.minimum_not_met_desc', {
								imagesCount: totalImages,
								minimumCount: spaceConfigs.raws_minimum_images
							})}
						</p>
					</div>
				)
		}
	}

	const isError = curationTouched && spaceChecks.spaceFlagRequired

	return (
		<div className='flex flex-col'>
			<div className='relative z-[1] flex w-full flex-col gap-1 px-6 py-4'>
				<h4 className='font-medium text-lg text-neutral-900'>
					{spaceNameToLabel[spaceName]}
				</h4>

				{totalImages === 0 && (
					<div className='flex items-center'>
						{type === 'noImages' && (
							<div
								className={cn(
									'box-border flex items-center rounded border px-2 py-1',
									isError
										? 'border-danger-200 bg-danger-100 [&>p]:text-danger-600 [&>svg]:fill-danger-600'
										: 'border-neutral-200 bg-neutral-100 [&>p]:text-neutral-600 [&>svg]:fill-neutral-600'
								)}
							>
								<p className='text-sm'>
									{t('photoset:curation.space.select_reason')}
								</p>
							</div>
						)}
					</div>
				)}
			</div>
			<Separator orientation='horizontal' />
			{unusableCurationSpaceBody()}
		</div>
	)
}

UnusableCurationPhotosetSpace.displayName = 'UnusableCurationPhotosetSpace'
